import { type CSSProperties, forwardRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Col, Flex, Image, Skeleton, Space, theme, Typography } from 'antd';
import type { CopyConfig } from 'antd/es/typography/Base';
import HomeHeaderImage from 'pos-client/assets/Avayler-Page-Header.png';
import { useDocumentTitle } from 'pos-client/hooks/useDocumentTitle.ts';
import { copyIcons } from 'pos-client/utils/copyIcons.tsx';

export type PosPageHeaderProps = {
  title?: ReactNode;
  titleCopy?: CopyConfig;
  documentTitle?: string;
  description?: string;
  actionComponent?: ReactNode;
  statusTag?: ReactNode;
  tabBar?: ReactNode;
  headerIcon?: ReactNode;
  style?: CSSProperties;
  loading?: boolean;
};

type PosPageWrapperProps = Pick<PosPageHeaderProps, 'style' | 'documentTitle'> &
  Partial<
    Record<Exclude<keyof PosPageHeaderProps, 'style' | 'documentTitle'>, never>
  >;

type PosPageHeaderStandard = {
  children?: never;
} & PosPageHeaderProps;

type PosPageHeaderWrapperType = {
  children: ReactNode;
} & PosPageWrapperProps;

type PosPageHeaderType = PosPageHeaderStandard | PosPageHeaderWrapperType;

const PageTitle = ({
  title,
  loading,
  headerIcon,
  statusTag,
  titleCopy,
}: Pick<
  PosPageHeaderProps,
  'loading' | 'title' | 'headerIcon' | 'statusTag' | 'titleCopy'
>) => (
  <Space size="large">
    {headerIcon && <>{loading ? <Skeleton.Avatar active /> : headerIcon}</>}
    {loading ? (
      <Skeleton.Input active block />
    ) : (
      <Typography.Title
        level={3}
        style={{
          marginBottom: 0,
        }}
        copyable={titleCopy}
      >
        {title}
      </Typography.Title>
    )}
    {statusTag && statusTag}
  </Space>
);

export const PosPageHeader = forwardRef<HTMLElement, PosPageHeaderType>(
  (
    {
      title,
      titleCopy,
      documentTitle,
      description,
      actionComponent,
      statusTag,
      tabBar,
      headerIcon,
      style,
      children,
      loading = false,
    },
    ref
  ) => {
    const { token } = theme.useToken();
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const { Text } = Typography;
    const hasDescription = !!description;

    const titleString = typeof title === 'string' ? title : undefined;

    const titleCopyProps = titleCopy
      ? {
          icon: copyIcons,
          ...titleCopy,
        }
      : titleCopy;

    useDocumentTitle(documentTitle ?? titleString ?? t('home.avaylerPos'));

    return (
      <Flex
        style={{
          paddingBottom: tabBar ? 0 : token.paddingSM,
          paddingTop: token.paddingSM,
          paddingLeft: token.paddingLG,
          paddingRight: token.paddingLG,
          backgroundColor: token.Layout?.bodyBg,
          boxShadow: tabBar ? '0 4px 14px 0 rgba(60, 78, 103, 0.5)' : undefined,
          width: '100%',
          ...style,
        }}
        gap="small"
        vertical
        ref={ref}
      >
        {children ? (
          children
        ) : (
          <>
            <Flex
              align="center"
              justify="space-between"
              style={{
                width: '100%',
                paddingBottom: tabBar ? token.paddingXS : 0,
              }}
            >
              <Flex vertical gap="small">
                <PageTitle
                  title={title}
                  titleCopy={titleCopyProps}
                  headerIcon={headerIcon}
                  statusTag={statusTag}
                  loading={loading}
                />
                {loading ? (
                  <Skeleton.Input
                    size="small"
                    active={loading && hasDescription}
                  />
                ) : (
                  <Text
                    hidden={!hasDescription}
                    style={{
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {description}
                  </Text>
                )}
              </Flex>
              {actionComponent && (
                <>{loading ? <Skeleton.Button active /> : actionComponent}</>
              )}
              {pathname === '/' && (
                <Col flex="0 1 256px">
                  <Image
                    src={HomeHeaderImage}
                    preview={false}
                    height="100%"
                    alt={t('home.bannerAltText')}
                  />
                </Col>
              )}
            </Flex>
            {tabBar && tabBar}
          </>
        )}
      </Flex>
    );
  }
);
